import type {AnyEntityClass} from "./Model.js";
import type {EntityRef} from "./types.js";

/**
 * Field schema. A field knows what a value *is* and, optionally, how one instance of a model
 * renders it — as a table cell or a form editor. It never knows what a "table" or "form" looks
 * like: `TNode` is whatever component type the app's renderers produce, so this file never
 * imports a UI library. `PresentationRegistry` supplies a renderer for fields that don't bring
 * their own, keyed by field kind.
 */

export type FieldKind =
    | "string"
    | "number"
    | "boolean"
    | "date"
    | "timestamp"
    | "duration"
    | "enum"
    | "json"
    | "file"
    | "relation"
    | "relationList";

export interface ValidationIssue {
    readonly field: Field<unknown, boolean>;
    readonly message: string;
}

export type Validator<TValue> = (value: TValue | null) => string | undefined;

/** The model a relation points at, resolved late so two models may reference each other. */
export type RelationTarget = () => AnyEntityClass;

/** What a renderer is given: the field itself, the value to show, and the entity it belongs to. */
export interface CellContext<TValue, TEntity, TContext = unknown> {
    readonly field: Field<TValue, boolean>;
    readonly value: TValue | null;
    readonly entity: TEntity;
    /** Whatever the call site needs to render this cell, e.g. the app code for building a link. */
    readonly context: TContext;
}

export interface EditorContext<TValue, TEntity, TContext = unknown> extends CellContext<TValue, TEntity, TContext> {
    readonly issues: readonly string[];

    setValue(value: TValue | null): void;
}

export type CellFactory<TValue, TEntity, TNode, TContext = unknown> =
    (context: CellContext<TValue, TEntity, TContext>) => TNode;
export type EditorFactory<TValue, TEntity, TNode, TContext = unknown> =
    (context: EditorContext<TValue, TEntity, TContext>) => TNode;

export interface FieldConfig<TValue> {
    readonly kind: FieldKind;
    readonly name: string | undefined;
    readonly label: string | undefined;
    readonly helpText: string | undefined;
    readonly nullable: boolean;
    readonly readOnly: boolean;
    readonly writeOnly: boolean;
    readonly defaultValue: TValue | null;
    readonly validators: readonly Validator<TValue>[];
    /** Selects a renderer registered under an explicit key instead of the one bound to the kind. */
    readonly presentationKey: string | undefined;
    readonly enumValues: readonly TValue[] | undefined;
    /** The related model, for relation and relationList fields. */
    readonly relationTarget: RelationTarget | undefined;
    /** Path to order by when this field is a table column, if different from its own name. */
    readonly orderBy: string | undefined;
    /** Type-erased `CellFactory`; `withCell`/`getCell` are the only doors in and out of it. */
    readonly cell: unknown;
    /** Type-erased `EditorFactory`; `withEditor`/`getEditor` are the only doors in and out of it. */
    readonly editor: unknown;
}

const baseConfig = <TValue>(kind: FieldKind): FieldConfig<TValue> => ({
    kind,
    name: undefined,
    label: undefined,
    helpText: undefined,
    nullable: true,
    readOnly: false,
    writeOnly: false,
    defaultValue: null,
    validators: [],
    presentationKey: undefined,
    enumValues: undefined,
    relationTarget: undefined,
    orderBy: undefined,
    cell: undefined,
    editor: undefined
});

/**
 * `TNullable` tracks whether the field may hold `null`, so `FieldValue` can report
 * the precise value type at the call site.
 */
export class Field<TValue, TNullable extends boolean = true> {
    constructor(readonly config: FieldConfig<TValue>) {
    }

    get kind(): FieldKind {
        return this.config.kind;
    }

    get name(): string | undefined {
        return this.config.name;
    }

    get label(): string {
        return this.config.label ?? this.config.name ?? "";
    }

    get nullable(): boolean {
        return this.config.nullable;
    }

    get readOnly(): boolean {
        return this.config.readOnly;
    }

    /** Path to order by when this field backs a table column; defaults to the field's own name. */
    get orderBy(): string {
        return this.config.orderBy ?? this.config.name ?? "";
    }

    named(name: string): Field<TValue, TNullable> {
        return this.#with({name});
    }

    withLabel(label: string): Field<TValue, TNullable> {
        return this.#with({label});
    }

    withHelpText(helpText: string): Field<TValue, TNullable> {
        return this.#with({helpText});
    }

    withDefault(defaultValue: TValue): Field<TValue, TNullable> {
        return this.#with({defaultValue});
    }

    withValidator(validator: Validator<TValue>): Field<TValue, TNullable> {
        return this.#with({validators: [...this.config.validators, validator]});
    }

    withPresentation(presentationKey: string): Field<TValue, TNullable> {
        return this.#with({presentationKey});
    }

    /** Overrides what a table column sorts by when it shows this field; e.g. a relation sorting by a denormalised name column. */
    withOrderBy(orderBy: string): Field<TValue, TNullable> {
        return this.#with({orderBy});
    }

    /**
     * How this field draws itself as a table cell. `TEntity` and `TNode` are whatever the caller
     * uses them for; the app decides both when it defines the field and again when it reads the
     * renderer back, so the type-erasure in between is safe.
     */
    withCell<TEntity, TNode, TContext = unknown>(cell: CellFactory<TValue, TEntity, TNode, TContext>): Field<TValue, TNullable> {
        return this.#with({cell});
    }

    /** How this field draws itself as a form editor; see {@link withCell}. */
    withEditor<TEntity, TNode, TContext = unknown>(editor: EditorFactory<TValue, TEntity, TNode, TContext>): Field<TValue, TNullable> {
        return this.#with({editor});
    }

    /** The cell renderer this field was given with {@link withCell}, if any. */
    getCell<TEntity, TNode, TContext = unknown>(): CellFactory<TValue, TEntity, TNode, TContext> | undefined {
        return this.config.cell as CellFactory<TValue, TEntity, TNode, TContext> | undefined;
    }

    /** The editor renderer this field was given with {@link withEditor}, if any. */
    getEditor<TEntity, TNode, TContext = unknown>(): EditorFactory<TValue, TEntity, TNode, TContext> | undefined {
        return this.config.editor as EditorFactory<TValue, TEntity, TNode, TContext> | undefined;
    }

    asReadOnly(): Field<TValue, TNullable> {
        return this.#with({readOnly: true});
    }

    asWriteOnly(): Field<TValue, TNullable> {
        return this.#with({writeOnly: true});
    }

    required(): Field<TValue, false> {
        return new Field<TValue, false>({...this.config, nullable: false});
    }

    validate(value: TValue | null): ValidationIssue[] {
        const issues: ValidationIssue[] = [];
        if (value === null || value === undefined) {
            if (!this.config.nullable) {
                issues.push({field: this as Field<unknown, boolean>, message: `${this.label || "Field"} is required`});
            }
            return issues;
        }
        for (const validator of this.config.validators) {
            const message = validator(value);
            if (message !== undefined) {
                issues.push({field: this as Field<unknown, boolean>, message});
            }
        }
        return issues;
    }

    #with(patch: Partial<FieldConfig<TValue>>): Field<TValue, TNullable> {
        return new Field<TValue, TNullable>({...this.config, ...patch});
    }
}

export type FieldValue<F> = F extends Field<infer TValue, infer TNullable>
    ? (TNullable extends true ? TValue | null : TValue)
    : never;

const withRange = <TValue extends number>(field: Field<TValue>, min?: number, max?: number): Field<TValue> => {
    let result = field;
    if (min !== undefined) {
        result = result.withValidator(value => (value !== null && value < min) ? `Must be at least ${min}` : undefined);
    }
    if (max !== undefined) {
        result = result.withValidator(value => (value !== null && value > max) ? `Must be at most ${max}` : undefined);
    }
    return result;
};

export const field = {
    string(): Field<string> {
        return new Field(baseConfig<string>("string"));
    },
    number(range?: { min?: number; max?: number }): Field<number> {
        return withRange(new Field(baseConfig<number>("number")), range?.min, range?.max);
    },
    boolean(): Field<boolean> {
        return new Field(baseConfig<boolean>("boolean"));
    },
    date(): Field<Date> {
        return new Field(baseConfig<Date>("date"));
    },
    timestamp(): Field<Date> {
        return new Field(baseConfig<Date>("timestamp"));
    },
    duration(): Field<number> {
        return new Field(baseConfig<number>("duration"));
    },
    file(): Field<File> {
        return new Field(baseConfig<File>("file"));
    },
    enumOf<TValue extends string>(values: readonly TValue[]): Field<TValue> {
        return new Field({...baseConfig<TValue>("enum"), enumValues: values});
    },
    /** A relation holding the id of the related entity; nested payloads are cached by `target`. */
    relation(target?: RelationTarget): Field<EntityRef> {
        return new Field({...baseConfig<EntityRef>("relation"), relationTarget: target});
    },
    relationList(target?: RelationTarget): Field<EntityRef[]> {
        return new Field({...baseConfig<EntityRef[]>("relationList"), relationTarget: target});
    }
} as const;
